import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { customerAPI, locationAPI } from '../../../services';
import { useApi, useAsyncAction } from '../../../hooks';
import { INDIA_STATES, getCitiesForState } from '../../../constants/indianLocations';
import {
  Button,
  Modal,
  Input,
  Select,
  ErrorMessage,
  SuccessMessage,
  Badge,
  Pagination,
  LoadingSpinner,
} from '../../UI';
import SearchInput from '../../UI/SearchInput';
import { Users, Plus, Eye, Edit, Phone, Mail, MapPin } from 'lucide-react';

const MyCustomersPage = () => {
  const { user } = useAuth();
  const { data: customersData, loading, refetch } = useApi(
    () => customerAPI.getCustomers({ addedBy: user?._id }),
    []
  );
  const { data: locationsData } = useApi(locationAPI.getLocations, []);
  
  const { loading: createLoading, execute: executeCreate } = useAsyncAction();
  const { loading: editLoading, execute: executeEdit } = useAsyncAction();
  
  const customers = customersData?.customers || [];
  const locations = locationsData?.locations || [];
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  
  const [form, setForm] = useState({
    name: '', phone: '', email: '', gender: '', maritalStatus: '',
    dateOfBirth: '', dateOfAnniversary: '', location: '', area: '',
    address: { street: '', city: '', state: '' },
    pincode: '', customerDesignation: '', source: 'Marketing', notes: '',
    interested: '', followUpDate: ''
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      if (search) {
        setSearchLoading(true);
        refetch({ search, addedBy: user?._id }).finally(() => setSearchLoading(false));
      } else {
        refetch({ addedBy: user?._id });
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const result = await executeCreate(customerAPI.createCustomer, form);
    if (result.success) {
      setSuccess('Customer added successfully');
      setIsAddModalOpen(false);
      setForm({ name: '', phone: '', email: '', gender: '', maritalStatus: '', dateOfBirth: '', dateOfAnniversary: '', location: '', area: '', address: { street: '', city: '', state: '' }, pincode: '', customerDesignation: '', source: 'Marketing', notes: '', interested: '', followUpDate: '' });
      // Force immediate refetch without filters
      setTimeout(() => refetch(), 100);
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Failed to add customer');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    const result = await executeEdit(customerAPI.updateCustomer, selected._id, form);
    if (result.success) {
      setSuccess('Customer updated');
      setIsEditModalOpen(false);
      // Force immediate refetch
      setTimeout(() => refetch(), 100);
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Update failed');
    }
  };

  const openEditModal = (c) => {
    setSelected(c);
    setForm({
      name: c.name || '', phone: c.phone || '', email: c.email || '',
      gender: c.gender || '', maritalStatus: c.maritalStatus || '',
      dateOfBirth: c.dateOfBirth?.split('T')[0] || '',
      dateOfAnniversary: c.dateOfAnniversary?.split('T')[0] || '',
      location: c.location || '', area: c.area || '',
      address: { street: c.address?.street || '', city: c.address?.city || '', state: c.address?.state || '' },
      pincode: c.pincode || '', customerDesignation: c.customerDesignation || '',
      source: c.source || 'Marketing', notes: c.notes || '',
      interested: c.interested || '', followUpDate: c.followUpDate?.split('T')[0] || ''
    });
    setIsEditModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {error && <ErrorMessage message={error} onClose={() => setError('')} />}
      {success && <SuccessMessage message={success} onClose={() => setSuccess('')} />}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">My Customers</h2>
          <p className="text-gray-500">Customers added by you</p>
        </div>
        <Button onClick={() => setIsAddModalOpen(true)} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add Customer
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Customers</p>
          <p className="text-2xl font-bold text-gray-900">{customers.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">This Week</p>
          <p className="text-2xl font-bold text-green-600">
            {customers.filter(c => {
              const weekAgo = new Date();
              weekAgo.setDate(weekAgo.getDate() - 7);
              return new Date(c.createdAt) > weekAgo;
            }).length}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">This Month</p>
          <p className="text-2xl font-bold text-blue-600">
            {customers.filter(c => {
              const monthAgo = new Date();
              monthAgo.setMonth(monthAgo.getMonth() - 1);
              return new Date(c.createdAt) > monthAgo;
            }).length}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Marketing</p>
          <p className="text-2xl font-bold text-purple-600">
            {customers.filter(c => c.source === 'Marketing').length}
          </p>
        </div>
      </div>

      {/* Search */}
      <SearchInput
        placeholder="Search your customers..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onClear={() => setSearch('')}
        isLoading={searchLoading}
        className="max-w-md"
      />

      {/* Customer Grid */}
      {loading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : customers.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {customers.map((c) => (
            <div key={c._id} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg">
                    {c.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{c.name}</h3>
                    <Badge variant={c.source === 'Marketing' ? 'primary' : 'success'} className="text-xs">
                      {c.source}
                    </Badge>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2 text-sm text-gray-600 mb-4">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <a href={`tel:${c.phone}`} className="text-blue-600 hover:text-blue-700 font-medium">
                    {c.phone}
                  </a>
                </div>
                {c.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <a href={`mailto:${c.email}`} className="text-blue-600 hover:text-blue-700">
                      {c.email}
                    </a>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  {c.location} - {c.area}
                </div>
              </div>
              
              <div className="flex gap-2 pt-3 border-t border-gray-100">
                <Button variant="outline" size="sm" onClick={() => window.open(`tel:${c.phone}`, '_self')} className="flex-1">
                  <Phone className="w-4 h-4 mr-1" /> Call
                </Button>
                <Button variant="outline" size="sm" onClick={() => { setSelected(c); setIsViewModalOpen(true); }} className="flex-1">
                  <Eye className="w-4 h-4 mr-1" /> View
                </Button>
                <Button variant="outline" size="sm" onClick={() => openEditModal(c)} className="flex-1">
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="font-medium text-gray-900 mb-1">No customers found</h3>
          <p className="text-gray-500 mb-4">{search ? `No results for "${search}"` : 'Start adding your customers'}</p>
          <Button onClick={() => setIsAddModalOpen(true)}>Add Customer</Button>
        </div>
      )}

      {/* Add Modal */}
      <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="Add Customer" size="lg">
        <form onSubmit={handleCreate} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input label="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <Input label="Phone *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
            <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Select label="Gender *" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} required>
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </Select>
            <Select label="Marital Status *" value={form.maritalStatus} onChange={(e) => setForm({ ...form, maritalStatus: e.target.value })} required>
              <option value="">Select Status</option>
              <option value="unmarried">Unmarried</option>
              <option value="married">Married</option>
            </Select>
            {form.maritalStatus === 'married' && (
              <Input label="Anniversary Date" type="date" value={form.dateOfAnniversary} onChange={(e) => setForm({ ...form, dateOfAnniversary: e.target.value })} required />
            )}
            <Input label="Date of Birth" type="date" value={form.dateOfBirth} onChange={(e) => setForm({ ...form, dateOfBirth: e.target.value })} />
            <Select label="State *" value={form.address.state} onChange={(e) => { const s = e.target.value; setForm({ ...form, address: { ...form.address, state: s, city: '' }, location: '' }); }} required>
              <option value="">Select State</option>
              {INDIA_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </Select>
            <Select label="City / Location *" value={form.location} onChange={(e) => { const c = e.target.value; setForm({ ...form, location: c, address: { ...form.address, city: c } }); }} required disabled={!form.address.state}>
              <option value="">{form.address.state ? 'Select City' : 'Select State first'}</option>
              {getCitiesForState(form.address.state).map(c => <option key={c} value={c}>{c}</option>)}
            </Select>
            <Input label="Area / Locality" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} placeholder="e.g. Banjara Hills, Jubilee Hills" />
            <Input label="Street / Plot / Door No" value={form.address.street} onChange={(e) => setForm({ ...form, address: { ...form.address, street: e.target.value } })} />
            <Input label="Pincode" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} />
            <Input label="Customer Designation" value={form.customerDesignation} onChange={(e) => setForm({ ...form, customerDesignation: e.target.value })} placeholder="e.g. Shop Owner" />
            <Select label="Source *" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} required>
              <option value="Marketing">Marketing</option>
              <option value="Instore">Instore</option>
              <option value="Online">Online</option>
              <option value="Referral">Referral</option>
            </Select>
            <Select label="Interested?" value={form.interested} onChange={(e) => setForm({ ...form, interested: e.target.value })}>
              <option value="">Select</option>
              <option value="yes">Yes - Interested</option>
              <option value="no">No - Not Interested</option>
              <option value="maybe">Maybe - Follow Up</option>
            </Select>
            <Input label="Follow-up Date" type="date" value={form.followUpDate} onChange={(e) => setForm({ ...form, followUpDate: e.target.value })} />
          </div>
          <Input label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" type="button" onClick={() => setIsAddModalOpen(false)}>Cancel</Button>
            <Button type="submit" loading={createLoading}>Add Customer</Button>
          </div>
        </form>
      </Modal>

      {/* View Modal */}
      <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} title="Customer Details" size="lg">
        {selected && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4 pb-4 border-b border-gray-200">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-2xl">
                {selected.name?.charAt(0)?.toUpperCase()}
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">{selected.name}</h3>
                {selected.businessName && <p className="text-gray-500">{selected.businessName}</p>}
                <Badge variant={selected.source === 'Marketing' ? 'primary' : 'success'} className="mt-1">{selected.source}</Badge>
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-3">Contact Information</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Phone</p>
                  <p className="font-medium text-gray-900">{selected.phone}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="font-medium text-gray-900">{selected.email || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Personal Info */}
            <div>
              <h4 className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-3">Personal Information</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Gender</p>
                  <p className="font-medium text-gray-900 capitalize">{selected.gender || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Marital Status</p>
                  <p className="font-medium text-gray-900 capitalize">{selected.maritalStatus || 'N/A'}</p>
                </div>
                {selected.dateOfBirth && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Date of Birth</p>
                    <p className="font-medium text-gray-900">{new Date(selected.dateOfBirth).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                  </div>
                )}
                {selected.dateOfAnniversary && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Anniversary</p>
                    <p className="font-medium text-gray-900">{new Date(selected.dateOfAnniversary).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Location & Address */}
            <div>
              <h4 className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-3">Location & Address</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Location</p>
                  <p className="font-medium text-gray-900">{selected.location || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Area</p>
                  <p className="font-medium text-gray-900">{selected.area || 'N/A'}</p>
                </div>
                {selected.address?.street && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Street</p>
                    <p className="font-medium text-gray-900">{selected.address.street}</p>
                  </div>
                )}
                {selected.address?.city && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">City</p>
                    <p className="font-medium text-gray-900">{selected.address.city}</p>
                  </div>
                )}
                {selected.address?.state && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">State</p>
                    <p className="font-medium text-gray-900">{selected.address.state}</p>
                  </div>
                )}
                {selected.pincode && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Pincode</p>
                    <p className="font-medium text-gray-900">{selected.pincode}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Marketing Details */}
            <div>
              <h4 className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-3">Marketing Details</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Source</p>
                  <Badge variant={selected.source === 'Marketing' ? 'primary' : selected.source === 'Instore' ? 'success' : 'warning'}>{selected.source}</Badge>
                </div>
                {selected.customerDesignation && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Designation</p>
                    <p className="font-medium text-gray-900">{selected.customerDesignation}</p>
                  </div>
                )}
                {selected.otherStoreName && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Other Store</p>
                    <p className="font-medium text-gray-900">{selected.otherStoreName}</p>
                  </div>
                )}
                {selected.visitDate && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Visit Date</p>
                    <p className="font-medium text-gray-900">{new Date(selected.visitDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                  </div>
                )}
                {selected.branchName && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Branch</p>
                    <p className="font-medium text-gray-900">{selected.branchName}</p>
                  </div>
                )}
                {selected.interested && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Interested</p>
                    <Badge variant={selected.interested === 'yes' ? 'success' : selected.interested === 'no' ? 'danger' : 'warning'}>
                      {selected.interested === 'yes' ? 'Yes - Interested' : selected.interested === 'no' ? 'No - Not Interested' : 'Maybe - Follow Up'}
                    </Badge>
                  </div>
                )}
                {selected.followUpDate && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Follow-up Date</p>
                    <p className="font-medium text-gray-900">{new Date(selected.followUpDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                  </div>
                )}
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Added On</p>
                  <p className="font-medium text-gray-900">{new Date(selected.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                </div>
              </div>
            </div>

            {selected.notes && (
              <div>
                <h4 className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-3">Notes</h4>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-gray-900">{selected.notes}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="Edit Customer" size="lg">
        <form onSubmit={handleEdit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input label="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <Input label="Phone *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
            <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Select label="Gender *" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} required>
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </Select>
            <Select label="Marital Status *" value={form.maritalStatus} onChange={(e) => setForm({ ...form, maritalStatus: e.target.value })} required>
              <option value="">Select Status</option>
              <option value="unmarried">Unmarried</option>
              <option value="married">Married</option>
            </Select>
            {form.maritalStatus === 'married' && (
              <Input label="Anniversary Date" type="date" value={form.dateOfAnniversary} onChange={(e) => setForm({ ...form, dateOfAnniversary: e.target.value })} />
            )}
            <Select label="State *" value={form.address.state} onChange={(e) => { const s = e.target.value; setForm({ ...form, address: { ...form.address, state: s, city: '' }, location: '' }); }} required>
              <option value="">Select State</option>
              {INDIA_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </Select>
            <Select label="City / Location *" value={form.location} onChange={(e) => { const c = e.target.value; setForm({ ...form, location: c, address: { ...form.address, city: c } }); }} required disabled={!form.address.state}>
              <option value="">{form.address.state ? 'Select City' : 'Select State first'}</option>
              {getCitiesForState(form.address.state).map(c => <option key={c} value={c}>{c}</option>)}
            </Select>
            <Input label="Area / Locality" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} placeholder="e.g. Banjara Hills" />
            <Input label="Street / Plot / Door No" value={form.address.street} onChange={(e) => setForm({ ...form, address: { ...form.address, street: e.target.value } })} />
            <Input label="Pincode" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} />
            <Input label="Customer Designation" value={form.customerDesignation} onChange={(e) => setForm({ ...form, customerDesignation: e.target.value })} />
            <Select label="Source *" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} required>
              <option value="Marketing">Marketing</option>
              <option value="Instore">Instore</option>
              <option value="Online">Online</option>
              <option value="Referral">Referral</option>
            </Select>
            <Select label="Interested?" value={form.interested} onChange={(e) => setForm({ ...form, interested: e.target.value })}>
              <option value="">Select</option>
              <option value="yes">Yes - Interested</option>
              <option value="no">No - Not Interested</option>
              <option value="maybe">Maybe - Follow Up</option>
            </Select>
            <Input label="Follow-up Date" type="date" value={form.followUpDate} onChange={(e) => setForm({ ...form, followUpDate: e.target.value })} />
          </div>
          <Input label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" type="button" onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
            <Button type="submit" loading={editLoading}>Update</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default MyCustomersPage;
